/**
 * Which folder on this machine holds each linked repository — desktop only.
 *
 * A path is this machine's business, so it stays here rather than on the server. The desktop
 * broker keeps its own list of folders the person picked and refuses any other; this is only the
 * page's memory of which picked folder is which repository, by the repository's name.
 */

import { repoFind } from './desktop'
import { normalizeRemote } from './openProjects'
import type { RepositoryLink } from './types'

const KEY = 'devpulse.repoFolders'

function read(): Record<string, string> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(KEY) ?? '{}')
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {}
  } catch {
    return {}
  }
}

/** Fired on window when a folder is remembered or forgotten (the desktop runner takes it up). */
export const FOLDERS_CHANGED = 'devpulse:repo-folders'

function write(map: Record<string, string>) {
  try {
    localStorage.setItem(KEY, JSON.stringify(map))
  } catch {
    // Private mode or storage blocked: the folder is simply asked for again next time.
  }
  window.dispatchEvent(new Event(FOLDERS_CHANGED))
}

/** Every project this machine knows, by name, with its folder. */
export function allFoldersByRepository(): Record<string, string> {
  return { ...read() }
}

/** Every project folder this machine knows. */
export function allFolders(): string[] {
  return Object.values(read())
}

export function folderFor(repository: string): string | null {
  return read()[repository.toLowerCase()] ?? null
}

export function rememberFolder(repository: string, path: string) {
  write({ ...read(), [repository.toLowerCase()]: path })
}

export function forgetFolder(repository: string) {
  const map = read()
  delete map[repository.toLowerCase()]
  write(map)
}

// ------------------------------------------------------------------------------ finding them here

/** The linked repositories already looked for in this app session, by id: one search each. */
const searched = new Set<string>()

/**
 * Looks for the linked repositories with no folder here yet, by their git remote (docs/refactor/53),
 * and remembers those found. Each is looked for once per app session. Returns the ones still missing.
 */
export async function findFoldersHere(linked: RepositoryLink[], again = false): Promise<RepositoryLink[]> {
  const missing = linked.filter((r) => !r.isDefault && !folderFor(r.name))
  const wanted = missing.filter((r) => again || !searched.has(r.id))
  if (wanted.length === 0) return missing
  for (const r of wanted) searched.add(r.id)
  const found = await repoFind(wanted.map((r) => ({ id: r.id, name: r.name, remoteUrl: r.remoteUrl ?? null }))).catch(() => [])
  for (const item of found) {
    const repository = wanted.find((r) => r.id === item.id)
    if (repository) rememberFolder(repository.name, item.folder.path)
  }
  return missing.filter((r) => !folderFor(r.name))
}

/**
 * Whether a folder picked for a repository is that repository: the same remote, when both have one.
 * A folder with no remote (or a repository linked without one) is taken at its word.
 */
export function folderMatches(repository: Pick<RepositoryLink, 'remoteUrl'>, remoteUrl: string | null): boolean {
  const want = normalizeRemote(repository.remoteUrl)
  const have = normalizeRemote(remoteUrl)
  return !want || !have || want === have
}
